import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode
} from "react";

import { cn, type WithClassNameAndChildren } from "~/lib/utils";

import { FilterChip } from "./filter-chip";

export type GroupFilterOption = { value: string; label: string };

export type GroupFilterLayerProps = {
  label: ReactNode;
  options: GroupFilterOption[];
  selected: string | null;
  onSelect: (value: string | null) => void;
};

type GroupFilterLayerColumnProps = GroupFilterLayerProps & {
  index: number;
  primarySelected: boolean;
  totalLayers: number;
};

const FilterColumn = ({
  label,
  children,
  className
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("flex min-w-0 flex-col gap-2", className)}>
    <span className="text-xs font-bold uppercase tracking-[0.08em] text-interface-neutral-4">
      {label}
    </span>
    <div className="flex flex-col gap-1.5">{children}</div>
  </div>
);

const GroupFilterLayerColumn = ({
  label,
  options,
  selected,
  onSelect,
  index,
  primarySelected,
  totalLayers
}: GroupFilterLayerColumnProps) => {
  const isSecondary = index === 1;

  if (isSecondary && !primarySelected) {
    return null;
  }

  const columnClassName =
    totalLayers === 2 && index === 0 && primarySelected
      ? "flex-none"
      : "flex-1";

  return (
    <FilterColumn label={label} className={columnClassName}>
      {options.map((option) => (
        <FilterChip
          key={option.value}
          label={option.label}
          selected={selected === option.value}
          onClick={() => {
            onSelect(selected === option.value ? null : option.value);
          }}
        />
      ))}
    </FilterColumn>
  );
};

/** Declarative slot — rendered by {@link GroupFilterRoot}. */
const GroupFilterLayer = (_props: GroupFilterLayerProps) => null;

const isGroupFilterLayer = (
  child: ReactNode
): child is ReactElement<GroupFilterLayerProps> =>
  isValidElement(child) && child.type === GroupFilterLayer;

const GroupFilterRoot = ({ children, className }: WithClassNameAndChildren) => {
  const layers = Children.toArray(children).filter(isGroupFilterLayer);

  if (layers.length < 1 || layers.length > 2) {
    throw new Error("GroupFilter expects 1 or 2 Layer children");
  }

  const primarySelected = layers[0].props.selected !== null;

  const secondaryLayer = layers.length === 2 ? layers[1] : null;

  return (
    <div className={cn("flex gap-3.5", className)}>
      {layers.map((layer, index) => {
        const onSelect =
          index === 0 && secondaryLayer
            ? (value: string | null) => {
                layer.props.onSelect(value);
                secondaryLayer.props.onSelect(null);
              }
            : layer.props.onSelect;

        return (
          <GroupFilterLayerColumn
            // eslint-disable-next-line react/no-array-index-key -- bounded, ordered layer list (max 2)
            key={index}
            {...layer.props}
            onSelect={onSelect}
            index={index}
            primarySelected={primarySelected}
            totalLayers={layers.length}
          />
        );
      })}
    </div>
  );
};

/**
 * One- or two-layer group selector. With two layers, the second column only
 * appears once a primary option is picked, and changing the primary selection
 * clears the secondary. Selecting an already-selected chip clears it.
 */
export const GroupFilter = Object.assign(GroupFilterRoot, {
  Layer: GroupFilterLayer
});
